import {
  useCallback,
  useEffect,
  useId,
  useRef,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";

/**
 * Beyond Sure — Dialog / Modal primitive (A1, portal).
 *
 * Portal-rendered modal with focus trap, scroll lock, Esc + backdrop
 * close. Use for: Add family member, Add policy quick-add, Settings
 * confirms (Delete account), generic confirmations.
 *
 * Slots:
 *   - title          : sentence-case heading (string OR ReactNode)
 *   - description    : optional supporting line
 *   - children       : body content
 *   - footer         : right-aligned action cluster (Save / Cancel)
 *
 * Sizes (max-width):
 *   sm  → 420  · md  → 560 (default)  · lg  → 720  · full → 100% with margins
 *
 * Accessibility:
 *   role="dialog" + aria-modal="true"
 *   aria-labelledby / aria-describedby wired to title/description IDs
 *   focus moves to first focusable element on open
 *   focus trap: Tab/Shift+Tab cycle inside the dialog
 *   Esc closes (unless `dismissOnEsc={false}`)
 *   focus returns to the previously focused element on close
 *   body scroll locked while open
 */

type Size = "sm" | "md" | "lg" | "full";

interface DialogProps {
  open: boolean;
  onClose: () => void;
  /** Title slot — heading text or node. */
  title?: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: Size;
  /** Default true — Esc closes. Set false for required confirmations. */
  dismissOnEsc?: boolean;
  /** Default true — clicking the backdrop closes. */
  dismissOnBackdrop?: boolean;
  /** Hide the × close button in the header. */
  hideCloseButton?: boolean;
  /** data-testid forwarded to the dialog. */
  "data-testid"?: string;
}

const SIZE_MAX_W: Record<Size, string> = {
  sm: "max-w-[420px]",
  md: "max-w-[560px]",
  lg: "max-w-[720px]",
  full: "max-w-[calc(100vw-32px)]",
};

export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = "md",
  dismissOnEsc = true,
  dismissOnBackdrop = true,
  hideCloseButton = false,
  "data-testid": testid,
}: DialogProps) {
  const titleId = useId();
  const descId = useId();
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const prevActiveRef = useRef<HTMLElement | null>(null);
  const reducedMotion = useReducedMotion();

  /* ── Body scroll lock + remember previously focused element ──── */
  useEffect(() => {
    if (!open) return;
    prevActiveRef.current = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // Focus first focusable element in the dialog.
    requestAnimationFrame(() => {
      const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      focusable?.[0]?.focus();
    });

    return () => {
      document.body.style.overflow = prevOverflow;
      requestAnimationFrame(() => prevActiveRef.current?.focus?.());
    };
  }, [open]);

  /* ── Esc + focus trap on Tab ──────────────────────────────────── */
  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Escape" && dismissOnEsc) {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key === "Tab") {
        const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        if (!focusable || focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;
        if (e.shiftKey && active === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
    [dismissOnEsc, onClose],
  );

  const portalRoot = typeof document !== "undefined" ? document.body : null;
  if (!portalRoot) return null;

  return createPortal(
    <AnimatePresence>
      {open ? (
        <div
          className="fixed inset-0 z-[1000] flex items-end sm:items-center justify-center p-0 sm:p-4"
          onKeyDown={onKeyDown}
        >
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reducedMotion ? 0.1 : 0.18 }}
            onClick={() => dismissOnBackdrop && onClose()}
            className="absolute inset-0 bg-[rgb(0_0_0_/_0.4)] backdrop-blur-[1px]"
            aria-hidden
          />

          {/* Dialog */}
          <motion.div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={title ? titleId : undefined}
            aria-describedby={description ? descId : undefined}
            data-testid={testid}
            initial={
              reducedMotion
                ? { opacity: 0 }
                : { opacity: 0, scale: 0.97, y: 12 }
            }
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={
              reducedMotion
                ? { opacity: 0 }
                : { opacity: 0, scale: 0.97, y: 12 }
            }
            transition={{ duration: reducedMotion ? 0.1 : 0.22, ease: [0.4, 0, 0.2, 1] }}
            className={[
              "relative w-full",
              SIZE_MAX_W[size],
              "rounded-t-[20px] sm:rounded-[16px]",
              "bg-[var(--color-paper)]",
              "border border-[var(--color-ink-08)]",
              "shadow-[0_24px_60px_-20px_rgba(0,0,0,0.32)]",
              "max-h-[92vh] flex flex-col overflow-hidden",
            ].join(" ")}
          >
            {/* Header */}
            {(title || !hideCloseButton) ? (
              <header className="flex items-start gap-3 px-5 sm:px-6 pt-5 sm:pt-6 pb-3">
                <div className="flex-1 min-w-0">
                  {title ? (
                    <h2
                      id={titleId}
                      className="bsure-display text-[18px] sm:text-[20px] font-bold leading-[1.25] text-[var(--color-ink)]"
                    >
                      {title}
                    </h2>
                  ) : null}
                  {description ? (
                    <p
                      id={descId}
                      className="mt-1 text-[13.5px] leading-[1.5] text-[var(--color-ink-60)]"
                    >
                      {description}
                    </p>
                  ) : null}
                </div>
                {!hideCloseButton ? (
                  <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close dialog"
                    className={[
                      "inline-flex items-center justify-center w-8 h-8 -mr-1 -mt-1 rounded-[8px]",
                      "text-[var(--color-ink-60)]",
                      "hover:bg-[var(--color-ink-08)] hover:text-[var(--color-ink)]",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)] focus-visible:ring-offset-2",
                      "transition-colors duration-[var(--duration-fast)]",
                    ].join(" ")}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      aria-hidden
                    >
                      <path
                        d="M6 6L18 18M6 18L18 6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                ) : null}
              </header>
            ) : null}

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-5 sm:px-6 pb-5 sm:pb-6">
              {children}
            </div>

            {/* Footer */}
            {footer ? (
              <footer
                className={[
                  "flex flex-wrap items-center justify-end gap-2",
                  "px-5 sm:px-6 py-3 sm:py-4",
                  "border-t border-[var(--color-ink-08)]",
                  "bg-[var(--color-ink-04)]",
                ].join(" ")}
              >
                {footer}
              </footer>
            ) : null}
          </motion.div>
        </div>
      ) : null}
    </AnimatePresence>,
    portalRoot,
  );
}

export default Dialog;
